// dataUtils.js
export const getAxisLabels = (chartId, xAxis, yAxis) => {
  const axisLabelMap = {
    // X-axis labels
    vehicle: 'Vehicles',
    route: 'Routes',
    time: 'Time Period',
    status: 'Status',

    // Y-axis labels
    count: 'Incidents',
    speed: 'Speed (km/h)',
    hours: 'Hours',
    deviations: 'Route Deviations',
    distance: 'Distance (km)',
    stops: 'No. of Stops',
    trips: 'Trips',
    dead: 'Dead Halts ',
    inactive: 'Inactive Halts ',
    duration: 'Duration (Minutes)',
    value: 'Value'
  };

  // Special cases for specific chart types
  const specialCases = {
    'trips_by_time_of_day': { x: 'Time Period', y: 'Trip Count' },
    'maximum_speed': { x: 'Vehicles', y: 'Speed (km/h)' },
    'average_speed': { x: 'Vehicles', y: 'Speed (km/h)' },
    'total_runtime': { x: 'Vehicles', y: 'Runtime (Hours)' },
    'halt_duration': { x: 'Vehicles', y: 'Duration (Hours)' },
    'dead_halt_duration': { x: 'Vehicles', y: 'Dead Halts Duration (Hours)' },
    'inactive_halt_duration': { x: 'Vehicles', y: 'Inactive Halts Duration (Hours)' }
  };

  if (specialCases[chartId]) {
    return {
      xAxisLabel: specialCases[chartId].x,
      yAxisLabel: specialCases[chartId].y
    };
  }

  return {
    xAxisLabel: axisLabelMap[xAxis] || xAxis,
    yAxisLabel: axisLabelMap[yAxis] || yAxis
  };
};

export const parseTimeToMinutes = (timeString) => {
  if (!timeString || typeof timeString !== "string") return 0;

  const hoursMatch = timeString.match(/(\d+)h/);
  const minutesMatch = timeString.match(/(\d+)m/);

  const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
  const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;

  return hours * 60 + minutes;
};

export const filterVehiclesWithData = (data, valueKey = "count") => {
  return data.filter((item) => {
    const value = item[valueKey];
    return value !== undefined && value !== null && value > 0;
  });
};

export const calculateTotalForChart = (data, chartId) => {
  if (!data || data.length === 0) return 0;

  switch (chartId) {
    case "overspeed_incidents":
      return data.reduce((sum, item) => sum + (item.count || 0), 0);
    case "total_runtime":
      const totalHours = Math.round(
        data.reduce((sum, item) => sum + (item.hours || 0), 0) * 100
      ) / 100;
      return `${totalHours} hrs`;
    case "total_trips":
      return data.reduce(
        (sum, item) =>
          sum +
          (item.onTime || 0) +
          (item.delayed || 0),
        0
      );
    case "trips_by_time_of_day":
      return null;
    case "trip_status":
      return data.reduce((sum, item) => sum + (item.count || 0), 0);
    case "total_halt":
      return data.reduce(
        (sum, item) => sum + (item.inactive || 0) + (item.dead || 0),
        0
      );
    case "halt_duration":
      const totalHaltDuration = Math.round(
        data.reduce(
          (sum, item) => sum + (item.inactive || 0) + (item.dead || 0),
          0
        ) * 100
      ) / 100;
      return `${totalHaltDuration} hrs`;
    case "stops_visited":
      return data.reduce((sum, item) => sum + (item.stops || 0), 0);
    case "trips_per_route":
      // FIXED: Sum onTime and delayed instead of looking for trips property
      return data.reduce((sum, item) => sum + (item.onTime || 0) + (item.delayed || 0), 0);
    case "dead_halt_count":
      return data.reduce((sum, item) => sum + (item.dead || 0), 0);
    case "inactive_halt_count":
      return data.reduce((sum, item) => sum + (item.inactive || 0), 0);
    case "dead_halt_duration":
      const totalDeadDuration = Math.round(
        data.reduce((sum, item) => sum + (item.dead || 0), 0) * 100
      ) / 100;
      return `${totalDeadDuration} hrs`;
    case "inactive_halt_duration":
      const totalInactiveDuration = Math.round(
        data.reduce((sum, item) => sum + (item.inactive || 0), 0) * 100
      ) / 100;
      return `${totalInactiveDuration} hrs`;
    case "distance_covered":
      const totalDistance = Math.round(
        data.reduce((sum, item) => sum + (item.distance || 0), 0) * 100
      ) / 100;
      return `${totalDistance} km`;
    case "average_speed":
      // Calculate the overall average speed across all vehicles
      const totalAvg = data.reduce((sum, item) => sum + (item.speed || 0), 0);
      const avgSpeed = Math.round((totalAvg / data.length) * 100) / 100;
      return `${avgSpeed} km/h`;
    default:
      return null;
  }
};

export const getVehicleNameFromDeviceId = (deviceId, vehiclesData) => {
  if (!vehiclesData || !Array.isArray(vehiclesData))
    return `Vehicle-${deviceId}`;

  const vehicle = vehiclesData.find((v) => v.device_id === deviceId);
  return vehicle ? vehicle.name : `Vehicle-${deviceId}`;
};

export const mapOverspeedDataForMultiVehicleChart = (reportData) => {
  if (!reportData?.data || !Array.isArray(reportData.data)) {
    return [];
  }

  const data = reportData.data;

  return data.map((item) => ({
    time: item.time,
    vehicleName: item.vehicleName || "Unknown Vehicle",
    speed: parseFloat(item.speed) || 0,
    date: item.date,
    timestamp: item.timestamp,
    latitude: item.latitude,
    longitude: item.longitude,
  }));
};